import { t } from '../i18n'
import type { StringKey } from '../i18n/en'
import { Notice } from './index'

/**
 * A refresh that failed behind a screen that already has its answer.
 *
 * The screens that read a reference list used to decide on the error before
 * they looked at the data, and React Query keeps the last successful answer
 * beside a failed refetch. So one 502 from the proxy while the container came
 * back replaced a half-filled form -- its typing, its choices and a photograph
 * already taken -- with a retry button, and pressing it gave the person a
 * fresh empty form. The lists it already had were still good and the failure
 * said nothing about them.
 *
 * The screen stays now, which leaves the other half: a list that could not be
 * refreshed is quietly a little older than the person thinks, and a silent
 * failure is the fault this project keeps finding. So it is said, above the
 * work rather than instead of it, with the retry on it.
 */
export function StaleNotice({
  error,
  retry,
  message = 'app.stale_note',
}: {
  error: unknown
  retry: () => void
  /**
   * Which sentence to say. The default is written for the reference lists a
   * form reads; a detail screen passes `app.stale_record`, because there the
   * notice can land directly under a report that an action succeeded and must
   * not be readable as that action having failed.
   */
  message?: StringKey
}) {
  if (!error) return null
  return (
    <Notice tone="warn">
      {t(message)}{' '}
      <button type="button" onClick={retry}>
        {t('app.retry')}
      </button>
    </Notice>
  )
}
